import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import Lenis from 'lenis'
import { landingConfig } from './config'

const FlowScene = lazy(() => import('./components/FlowScene').then((module) => ({
  default: module.FlowScene,
})))

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h13m-5-5 5 5-5 5" />
    </svg>
  )
}

function TelegramIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m3.5 11.2 16.8-6.5-3.1 14.6-5-3.7-2.7 2.6.4-4.7 7.9-6.2-9.7 5.1-4.6-1.2Z" />
    </svg>
  )
}

/** Splits a headline into masked lines so each one can rise into view. */
function RevealLines({ lines }: { lines: string[] }) {
  return (
    <>
      {lines.map((line) => (
        <span className="line-mask" key={line}>
          <span className="line-inner">{line}</span>
        </span>
      ))}
    </>
  )
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return reduced
}

/**
 * Eases the page toward the scroll target instead of snapping to it, which is
 * what gives the page its weight. Lenis animates the real scroll position, so
 * `position: sticky`, the native scrollbar and scroll listeners keep working.
 * Touch keeps the OS's own inertia — stacking a second one feels wrong.
 */
function useSmoothScroll(enabled: boolean) {
  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    const lenis = new Lenis({ duration: 1.05, wheelMultiplier: 0.9 })
    let frame = requestAnimationFrame(function raf(time) {
      lenis.raf(time)
      frame = requestAnimationFrame(raf)
    })

    // Anchor links must go through Lenis, otherwise they jump instantly.
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.('a[href^="#"]')
      const href = anchor?.getAttribute('href')

      if (!href || href.length < 2) {
        return
      }

      const target = document.querySelector(href)

      if (target) {
        event.preventDefault()
        lenis.scrollTo(target as HTMLElement)
      }
    }

    document.addEventListener('click', onClick)

    return () => {
      document.removeEventListener('click', onClick)
      cancelAnimationFrame(frame)
      lenis.destroy()
    }
  }, [enabled])
}

/**
 * Pulls an element toward the cursor while it is hovered. Exposed as CSS
 * custom properties so stylesheets keep ownership of the actual transform.
 * Skipped for coarse pointers and reduced-motion users.
 */
function useMagnetic<T extends HTMLElement>(strength = 0.28) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current

    if (
      !element ||
      !window.matchMedia('(pointer: fine)').matches ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined
    }

    const onMove = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect()
      element.style.setProperty(
        '--magnet-x',
        `${(event.clientX - (rect.left + rect.width / 2)) * strength}px`,
      )
      element.style.setProperty(
        '--magnet-y',
        `${(event.clientY - (rect.top + rect.height / 2)) * strength}px`,
      )
    }

    const reset = () => {
      element.style.setProperty('--magnet-x', '0px')
      element.style.setProperty('--magnet-y', '0px')
    }

    element.addEventListener('pointermove', onMove)
    element.addEventListener('pointerleave', reset)

    return () => {
      element.removeEventListener('pointermove', onMove)
      element.removeEventListener('pointerleave', reset)
    }
  }, [strength])

  return ref
}

/**
 * One small drawing per feature row. They are inline SVG rather than icons:
 * each one is the shape of the thing it describes — a spent-so-far ring, the
 * category bars, a balance trace — so the list carries a little of the product
 * instead of six identical glyphs. Animation is driven by the row's reveal.
 */
function FeatureGraphic({ index }: { index: string }) {
  if (index === '01') {
    // The day's allowance: month elapsed as an arc, today's slice highlighted.
    return (
      <svg className="feature-graphic" viewBox="0 0 56 56" aria-hidden="true">
        <circle className="ring-track" cx="28" cy="28" r="20" />
        <circle className="ring-fill" cx="28" cy="28" r="20" />
        <circle className="ring-today" cx="28" cy="28" r="20" />
      </svg>
    )
  }

  if (index === '02') {
    return (
      <svg className="feature-graphic" viewBox="0 0 56 56" aria-hidden="true">
        <rect className="bar" x="6" y="34" width="10" height="16" rx="2" />
        <rect className="bar" x="23" y="22" width="10" height="28" rx="2" />
        <rect className="bar" x="40" y="29" width="10" height="21" rx="2" />
      </svg>
    )
  }

  if (index === '03') {
    return (
      <svg className="feature-graphic" viewBox="0 0 56 56" aria-hidden="true">
        <line className="axis" x1="4" y1="28" x2="52" y2="28" />
        <path className="trace trace-in" d="M6 24 L18 14 L30 20 L42 8 L50 12" />
        <path className="trace trace-out" d="M6 33 L18 41 L30 36 L42 46 L50 42" />
      </svg>
    )
  }

  if (index === '04') {
    return (
      <svg className="feature-graphic" viewBox="0 0 56 56" aria-hidden="true">
        <rect className="sheet" x="7" y="10" width="42" height="36" rx="4" />
        <line className="rule" x1="7" y1="21" x2="49" y2="21" />
        <line className="rule" x1="7" y1="32" x2="49" y2="32" />
        <line className="rule" x1="21" y1="10" x2="21" y2="46" />
      </svg>
    )
  }

  if (index === '05') {
    return (
      <svg className="feature-graphic" viewBox="0 0 56 56" aria-hidden="true">
        <rect className="sheet" x="10" y="8" width="36" height="40" rx="8" />
        <path className="bolt" d="M30 17 L22 30 L28 30 L26 39 L34 26 L28 26 Z" />
      </svg>
    )
  }

  return (
    <svg className="feature-graphic" viewBox="0 0 56 56" aria-hidden="true">
      <rect className="sheet" x="8" y="12" width="40" height="28" rx="6" />
      <path className="bolt" d="M14 22 L28 31 L42 22" />
    </svg>
  )
}

const productShots = [
  { src: '/screens/overview-top.webp', alt: 'Экран «Обзор»: доступный баланс, доходы и расходы за месяц' },
  { src: '/screens/overview-breakdown.webp', alt: 'Экран «Обзор»: дневной лимит и разбивка расходов по категориям' },
  { src: '/screens/transactions.webp', alt: 'Экран «Операции»: список поступлений и списаний за месяц' },
]

/**
 * Real captures of the Mini App, not a rebuilt imitation of it. The scroll
 * position picks which one is on top; all three stay mounted so switching is a
 * crossfade rather than a load.
 */
function ProductShots({ frame }: { frame: number }) {
  return (
    <div className="product-shots">
      <figure className="shot shot-back" aria-hidden="true">
        <span className="shot-screen">
          <img
            src="/screens/accounts.webp"
            alt=""
            width={780}
            height={1688}
            loading="lazy"
            decoding="async"
          />
        </span>
        <span className="shot-glass" />
      </figure>

      <figure className="shot shot-front" data-theatre-hover>
        <span className="shot-screen">
          {productShots.map((shot, index) => (
            <img
              key={shot.src}
              className={index === frame ? 'is-active' : undefined}
              src={shot.src}
              alt={index === frame ? shot.alt : ''}
              width={780}
              height={1688}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          ))}
        </span>
        <span className="shot-glass" aria-hidden="true" />
      </figure>
    </div>
  )
}

function App() {
  const reducedMotion = useReducedMotion()
  const progressRef = useRef(0)
  const pointerRef = useRef({ x: 0, y: 0 })
  const hoverRef = useRef(false)
  const heroCtaRef = useMagnetic<HTMLAnchorElement>()
  const finalCtaRef = useMagnetic<HTMLAnchorElement>(0.18)
  const [shotIndex, setShotIndex] = useState(0)
  const shotIndexRef = useRef(0)

  useSmoothScroll(!reducedMotion)

  useEffect(() => {
    let frame = 0
    const product = document.getElementById('product')
    const composition = product?.querySelector<HTMLElement>('.product-shots')

    const updateProgress = () => {
      frame = 0
      const scrollable = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1)
      const progress = Math.min(Math.max(window.scrollY / scrollable, 0), 1)
      progressRef.current = progress
      document.documentElement.style.setProperty('--page-progress', progress.toFixed(4))
      document.documentElement.style.setProperty(
        '--hero-visibility',
        String(Math.max(0, 1 - progress * 5.5)),
      )

      if (!product || !composition) {
        return
      }

      /* Measured on the composition, not the section: the section is far taller
         than the screens, so section-relative progress burned through every
         frame while they were still below the fold.
         The travel deliberately spans the whole time the composition is on
         screen — entering from the bottom to leaving past the top — rather than
         only the window where it fits entirely. That window is barely 300px
         tall (the screens are nearly viewport height), which meant all three
         captures flicked past in a flick of the wheel. */
      const rect = composition.getBoundingClientRect()
      const travel = window.innerHeight * 0.62 + rect.height
      const local = Math.min(
        Math.max((window.innerHeight * 0.82 - rect.top) / travel, 0),
        1,
      )
      product.style.setProperty('--product-progress', local.toFixed(4))

      const total = productShots.length
      const next = Math.min(Math.floor(local * total), total - 1)

      if (next !== shotIndexRef.current) {
        shotIndexRef.current = next
        setShotIndex(next)
      }
    }

    const onScroll = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(updateProgress)
      }
    }

    updateProgress()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  useEffect(() => {
    const updatePointer = (event: PointerEvent) => {
      pointerRef.current.x = (event.clientX / window.innerWidth) * 2 - 1
      pointerRef.current.y = -((event.clientY / window.innerHeight) * 2 - 1)
      hoverRef.current = Boolean(
        (event.target as Element | null)?.closest?.('[data-theatre-hover]'),
      )
    }

    const resetPointer = () => {
      hoverRef.current = false
    }

    const updateFocus = (event: FocusEvent) => {
      hoverRef.current = Boolean(
        (event.target as Element | null)?.closest?.('[data-theatre-hover]'),
      )
    }

    const resetFocus = (event: FocusEvent) => {
      hoverRef.current = Boolean(
        (event.relatedTarget as Element | null)?.closest?.('[data-theatre-hover]'),
      )
    }

    window.addEventListener('pointermove', updatePointer, { passive: true })
    document.documentElement.addEventListener('pointerleave', resetPointer)
    document.addEventListener('focusin', updateFocus)
    document.addEventListener('focusout', resetFocus)

    return () => {
      window.removeEventListener('pointermove', updatePointer)
      document.documentElement.removeEventListener('pointerleave', resetPointer)
      document.removeEventListener('focusin', updateFocus)
      document.removeEventListener('focusout', resetFocus)
    }
  }, [])

  useEffect(() => {
    const sections = [...document.querySelectorAll<HTMLElement>('[data-reveal]')]
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
        }
      }),
      { threshold: 0.22 },
    )

    sections.forEach((section) => observer.observe(section))
    return () => observer.disconnect()
  }, [])

  return (
    <div className={`landing${reducedMotion ? ' is-reduced' : ''}`}>
      <a className="skip-link" href="#main-content">К основному содержанию</a>
      <Suspense fallback={<div className="scene-layer"><div className="scene-fallback" /></div>}>
        <FlowScene
          progressRef={progressRef}
          pointerRef={pointerRef}
          hoverRef={hoverRef}
          reducedMotion={reducedMotion}
        />
      </Suspense>
      <div className="scene-scrim" aria-hidden="true" />
      <div className="copy-scrim" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />

      <header className="site-header">
        <a
          className="brand"
          href="#top"
          aria-label="Finance Tracker — в начало"
          data-theatre-hover
        >
          <img
            className="brand-mark"
            src="/favicon.png"
            alt=""
            width="512"
            height="512"
          />
          <span>Finance Tracker</span>
        </a>
        <nav aria-label="Основная навигация">
          <a href="#rhythm" data-theatre-hover>Принцип</a>
          <a href="#features" data-theatre-hover>Возможности</a>
          <a href="#product" data-theatre-hover>Интерфейс</a>
        </nav>
        <div className="header-actions">
          <a
            className="header-telegram"
            href={landingConfig.telegramUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Запросить доступ в Telegram"
            data-theatre-hover
          >
            <TelegramIcon />
          </a>
        </div>
      </header>

      <main id="main-content">
        <section className="hero-section" id="top" data-reveal>
          <div className="hero-index">01 / 05</div>
          <div className="hero-copy">
            <p className="eyebrow">Личные финансы · приватно</p>
            <h1>
              <span className="line-mask"><span className="line-inner">Finance</span></span>
              <span className="line-mask"><span className="line-inner">Tracker</span></span>
            </h1>
            {/* Concrete before the mood: the page used to reach the third screen
                before naming a single thing the product actually does. */}
            <ul className="hero-facts">
              {landingConfig.heroFacts.map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
            <div className="hero-support">
              <p>Доходы, расходы и планы — в одной спокойной, управляемой системе.</p>
              <a
                ref={heroCtaRef}
                className="primary-cta"
                href={landingConfig.telegramUrl}
                target="_blank"
                rel="noreferrer"
                data-theatre-hover
              >
                <span>Запросить доступ</span>
                <ArrowIcon />
              </a>
            </div>
          </div>
          <a className="scroll-cue" href="#rhythm" data-theatre-hover>
            <span>Исследовать</span>
            <i aria-hidden="true" />
          </a>
        </section>

        <section className="story-section" id="rhythm" data-reveal>
          <div className="section-index">02 / 05</div>
          <div className="story-copy">
            <p className="eyebrow">Приватность</p>
            <h2><RevealLines lines={['Деньги остаются', 'вашими.']} /></h2>
            <p className="section-body">
              Finance Tracker не заводит собственную базу с вашими операциями.
              Он работает поверх таблицы, которая уже принадлежит вам.
            </p>
            {/* The route itself is the argument: the key to the sheet lives on
                the middle node and never reaches the first one. */}
            <ol className="data-flow" aria-label="Путь запроса от приложения к вашей таблице">
              {landingConfig.dataFlow.map((node, index) => (
                <li key={node.title} data-theatre-hover>
                  <span className="flow-link" aria-hidden="true">
                    <b className="flow-dot" />
                    {index < landingConfig.dataFlow.length - 1 && <i className="flow-wire" />}
                  </span>
                  <span className="flow-step">{node.step}</span>
                  <h3>{node.title}</h3>
                  <p>{node.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="features-section" id="features" data-reveal>
          <div className="section-index">03 / 05</div>
          <div className="features-head">
            <p className="eyebrow">Что внутри</p>
            <h2><RevealLines lines={['Как устроен', 'ваш учёт.']} /></h2>
          </div>
          <ol className="features-list">
            {landingConfig.features.map((feature) => (
              <li key={feature.index} data-theatre-hover>
                <span className="feature-index">{feature.index}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <FeatureGraphic index={feature.index} />
              </li>
            ))}
          </ol>
        </section>

        <section className="product-section" id="product" data-reveal>
          <div className="product-intro">
            <div className="section-index">04 / 05</div>
            <div>
              <p className="eyebrow">Всё важное — рядом</p>
              <h2><RevealLines lines={['Контроль без', 'лишнего шума.']} /></h2>
            </div>
            <p className="section-body">
              Баланс, безопасный дневной лимит и структура трат —
              в интерфейсе, который читается с первого взгляда.
            </p>
          </div>
          <ProductShots frame={shotIndex} />
          <p className="demo-note">Реальные экраны приложения с демонстрационными данными</p>
        </section>

        <section className="final-section" id="access" data-reveal>
          <div className="section-index">05 / 05</div>
          <div className="final-copy">
            <p className="eyebrow">Закрытый доступ</p>
            <h2><RevealLines lines={['Начните видеть', 'деньги целиком.']} /></h2>
            <p>
              Finance Tracker открывается для новых пользователей постепенно.
              Оставьте заявку в Telegram — пришлём доступ.
            </p>
            <dl className="final-meta">
              {landingConfig.accessMeta.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
            <a
              ref={finalCtaRef}
              className="final-cta"
              href={landingConfig.telegramUrl}
              target="_blank"
              rel="noreferrer"
              data-theatre-hover
            >
              <span className="final-cta-icon"><TelegramIcon /></span>
              <span><small>Написать в Telegram</small><strong>Запросить доступ</strong></span>
              <ArrowIcon />
            </a>
          </div>
          <footer>
            <span>
              © {new Date().getFullYear()} Finance Tracker ·{' '}
              <a href="https://t.me/alxndrorig" target="_blank" rel="noreferrer">
                by alxndrorig
              </a>
            </span>
            <span>Создано ради спокойствия за деньги</span>
          </footer>
        </section>
      </main>

      <div className="progress-rail" aria-hidden="true">
        <span><i /></span>
      </div>
    </div>
  )
}

export default App
